import { queryOptions } from '@tanstack/react-query'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const REQUEST_TIMEOUT_MS = 30_000

export class PortalError extends Error {
  status: number
  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'PortalError'
    this.status = status
  }
}

// Public /portal/{token} response only. Verified against the actual portal router
// by public-contract.test.ts; intentionally independent of dashboard contracts.
export type PortalStage =
  | 'ordered'
  | 'processing'
  | 'on_the_way'
  | 'delivered'
  | 'cancelled'
  | 'returned'

export type PortalItem = {
  name: string
  model: string | null
  image_url: string | null
  quantity: number
  options: string[]
  /** Set once delivered: the line a warranty claim is filed for. */
  line_id: number | null
}

export type PortalTracking = {
  number: string
  url: string | null
}

export type PortalShipmentUpdate = {
  label: string
  location: string | null
  on: string | null
}

export type PortalDelivery = {
  stage: PortalStage
  brand: string | null
  carrier: string | null
  freight: boolean
  tracking: PortalTracking[]
  pickup: boolean
  ship_on: string | null
  ship_estimated: boolean
  delivery_on: string | null
  delivery_estimated: boolean
  latest_update: PortalShipmentUpdate | null
  items: PortalItem[]
}

export type PortalProtectionStatus =
  | 'waiting_for_delivery'
  | 'needs_serial'
  | 'activating'
  | 'check_serial'
  | 'active'
  | 'cancelled'

export type PortalProtectedUnit = {
  id: string
  name: string
  model: string | null
  image_url: string | null
  unit: number
  of: number
  term_months: number
  covered_through: string
  status: PortalProtectionStatus
  serial: string | null
  policy_number: string | null
  has_photo: boolean
  can_edit: boolean
}

export type PortalWarrantyClaimStatus =
  | 'submitted'
  | 'with_manufacturer'
  | 'approved'
  | 'resolved'
  | 'declined'

export type PortalWarrantyClaim = {
  id: string
  number: string
  line_id: number
  name: string
  model: string | null
  image_url: string | null
  problem: string
  submitted_on: string
  status: PortalWarrantyClaimStatus
  note: string | null
  manufacturer_claim_number: string | null
}

export type PortalOrder = {
  order_number: string | null
  placed_on: string
  ship_to_city: string | null
  ship_to_state: string | null
  stage: PortalStage
  delivery_on: string | null
  delivery_estimated: boolean
  /** The date the customer asked to have everything by, from their quote. */
  deliver_by: string | null
  deliveries: PortalDelivery[]
  protection: PortalProtectedUnit[]
  warranty_claims: PortalWarrantyClaim[]
}

export type PortalContact = {
  name: string | null
  email: string | null
  phone: string | null
}

export type PortalProject = {
  contact: PortalContact
  orders: PortalOrder[]
}

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { signal: controller.signal })
  } finally {
    clearTimeout(id)
  }
}

async function portalError(res: Response): Promise<PortalError> {
  let detail = res.statusText
  try {
    const body = (await res.json()) as { detail?: string }
    if (body.detail) detail = body.detail
  } catch {
    // ignore parse error — fall back to statusText
  }
  return new PortalError(res.status, detail)
}

export async function fetchPortalProject(token: string): Promise<PortalProject> {
  const res = await fetchWithTimeout(
    `${API_BASE_URL}/portal/${encodeURIComponent(token)}`,
    REQUEST_TIMEOUT_MS,
  )
  if (!res.ok) throw await portalError(res)
  return normalizePortalProject(await res.json())
}

/** Save one covered unit's serial number (and rating-plate photo) to activate its protection plan. */
export async function addProtectionSerial(
  token: string,
  unitId: string,
  serial: string,
  photo: File | null,
): Promise<PortalProtectedUnit> {
  const body = new FormData()
  body.set('serial', serial)
  if (photo) body.set('photo', photo)
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const res = await fetch(
      `${API_BASE_URL}/portal/${encodeURIComponent(token)}/protection/${encodeURIComponent(unitId)}`,
      { method: 'POST', body, signal: controller.signal },
    )
    if (!res.ok) throw await portalError(res)
    return (await res.json()) as PortalProtectedUnit
  } finally {
    clearTimeout(id)
  }
}

export type WarrantyClaimInput = {
  lineId: number
  problem: string
  unitDown: boolean
  serial: string
  contactName: string
  contactEmail: string
  contactPhone: string
  photos: File[]
}

// Photos take a while to upload on a phone connection.
const CLAIM_TIMEOUT_MS = 120_000

/** File a warranty claim for one delivered item, with photos of the problem (USA-915). */
export async function fileWarrantyClaim(
  token: string,
  input: WarrantyClaimInput,
): Promise<PortalWarrantyClaim> {
  const body = new FormData()
  body.set('line_id', String(input.lineId))
  body.set('problem', input.problem)
  body.set('unit_down', String(input.unitDown))
  if (input.serial) body.set('serial', input.serial)
  body.set('contact_name', input.contactName)
  body.set('contact_email', input.contactEmail)
  body.set('contact_phone', input.contactPhone)
  for (const photo of input.photos) body.append('photos', photo)
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), CLAIM_TIMEOUT_MS)
  try {
    const res = await fetch(`${API_BASE_URL}/portal/${encodeURIComponent(token)}/warranty-claims`, {
      method: 'POST',
      body,
      signal: controller.signal,
    })
    if (!res.ok) throw await portalError(res)
    return (await res.json()) as PortalWarrantyClaim
  } finally {
    clearTimeout(id)
  }
}

// The response the API sent before USA-1167. The Projects app can publish before the backend
// release reaches production, so the tracker renders it plainly until the new response arrives.
type LegacyPortalOrder = {
  order_number: string | null
  status: string
  ship_to_city: string | null
  ship_to_state: string | null
  created_at: string
  line_items: { title: string; quantity: number }[]
}

const LEGACY_STAGE: Record<string, PortalStage> = {
  pending: 'ordered',
  shipped: 'on_the_way',
  partially_shipped: 'on_the_way',
  in_transit: 'on_the_way',
  partially_delivered: 'on_the_way',
  delivered: 'delivered',
  fulfilled: 'delivered',
  cancelled: 'cancelled',
  return_in_progress: 'returned',
  exchange_in_progress: 'returned',
}

export function normalizePortalProject(raw: unknown): PortalProject {
  const data = raw as Partial<PortalProject> & { orders?: unknown[] }
  const current =
    data.contact !== undefined &&
    (data.orders ?? []).every((order) => Array.isArray((order as PortalOrder).deliveries))
  if (current) {
    // Protection plans and warranty claims arrived after USA-1167: an API without them lists none.
    const project = data as PortalProject
    return {
      ...project,
      orders: project.orders.map((order) => ({
        ...order,
        protection: order.protection ?? [],
        warranty_claims: order.warranty_claims ?? [],
      })),
    }
  }
  return {
    contact: { name: null, email: 'status@shopusars.com', phone: '(888) 307-5030' },
    orders: ((data.orders ?? []) as LegacyPortalOrder[]).map((order) => {
      const stage = LEGACY_STAGE[order.status] ?? 'processing'
      return {
        order_number: order.order_number,
        placed_on: order.created_at.slice(0, 10),
        ship_to_city: order.ship_to_city,
        ship_to_state: order.ship_to_state,
        stage,
        delivery_on: null,
        delivery_estimated: false,
        deliver_by: null,
        protection: [],
        warranty_claims: [],
        deliveries: [
          {
            stage,
            brand: null,
            carrier: null,
            freight: false,
            tracking: [],
            pickup: false,
            ship_on: null,
            ship_estimated: false,
            delivery_on: null,
            delivery_estimated: false,
            latest_update: null,
            items: (order.line_items ?? []).map((item) => ({
              name: item.title,
              model: null,
              image_url: null,
              quantity: item.quantity,
              options: [],
              line_id: null,
            })),
          },
        ],
      }
    }),
  }
}

export function portalProjectQueryOptions(token: string) {
  return queryOptions({
    queryKey: ['portal', token],
    queryFn: () => fetchPortalProject(token),
    // Poll while Safeware activates a plan the customer just added a serial for.
    refetchInterval: (query) =>
      query.state.data?.orders.some((order) =>
        order.protection.some((unit) => unit.status === 'activating'),
      )
        ? 4_000
        : false,
  })
}
